import { Avatar, Flex, Tag, Text, useDisclosure } from '@chakra-ui/core'
import { NoFocusButton } from 'components/CommonComponents/NoFocusButton'
import { TooltipList } from 'components/CommonComponents/TooltipList'
import LocaleText from 'components/LocaleText'
import { PostModal } from 'components/NewsFeed/PostModal'
import { PostAdminsModal } from 'components/Post/PostAdminsModal'
import { PostMenu } from 'components/Post/PostHeader/PostMenu'
import ServicaCallOveviewModal from 'components/ServiceCalls/OverviewModal'
import { AppDispatch } from 'config/redux'
import { IConfigStateGroups, ITag, RootState } from 'constants/interfaces'
import keys from 'constants/keys'
import { useUserName } from 'hooks/useUserName'
import { ChevronLeft, Wrench } from 'lucide-react'
import React, { memo, useCallback, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getServiceCall, ServiceCall } from 'redux/actions/serviceCall'
import { Post } from 'redux/reducers/feed'
import { isRtlSelector } from 'redux/selectors/general'
import { findGroupByUid, findTagByUid, trimAndCountExtraGroups } from 'utils'

interface IChatHeader {
  chat: Post
  onClose: () => void
}

const SERVICE_CALL_REF_TYPE = 1
const SERVICE_CALL_BUTTON_COLORS = {
  bg: '#DDCCE9',
  iconFill: 'white',
}
const SERVICE_CALL_BUTTON_POSITIONS = {
  left: '57px',
  top: '47px',
}

export const ChatModalHeader = memo(({ chat, onClose }: IChatHeader) => {
  const { images, title, groups, users, post_id: chatId, created_by, tags } = chat
  const dispatch: AppDispatch = useDispatch()

  const uid = useSelector((state: RootState) => state.auth.uid)
  const isRtl = useSelector(isRtlSelector)
  const theme = useSelector((state: RootState) => state.general.theme)
  const {
    permission,
    tags: configTags,
    user_groups,
    groups: configGroups,
    retailUsersObject,
  } = useSelector((state: RootState) => state.config.config)
  const { serviceCalls, isLoading } = useSelector((state: RootState) => state.serviceCalls)

  const editPostModal = useDisclosure()
  const setAdminModal = useDisclosure()
  const serviceCallModal = useDisclosure()

  const chatCreator = useUserName(created_by)
  const [selectedServiceCall, setSelectedServiceCall] = useState<ServiceCall | null>(null)

  const permissionPosts = permission?.posts
  const isAdminPostPermission = useMemo(() => permissionPosts === keys.USER_ACTIONS_MAP.APP_ADMIN, [permissionPosts])

  const refType = chat?.ref_type
  const isExistServiceCall = useMemo(() => refType === SERVICE_CALL_REF_TYPE, [refType])

  const postType = chat.type
  const isGroupPost = useMemo(
    () => postType === keys.POST_TYPE.GROUP_POST || postType === keys.POST_TYPE.GROUP_POST_ANNOUNCEMENT,
    [postType]
  )

  const postCreatorId = chat.created_by
  const canEditPost = useMemo(
    () => !isExistServiceCall && (postCreatorId === uid || isAdminPostPermission),
    [isExistServiceCall, postCreatorId, uid, isAdminPostPermission]
  )

  const isPostCreator = useMemo(() => postCreatorId === uid, [postCreatorId, uid])

  const calculateContextIdForGetServiceCall = useCallback((): string | null => {
    const matchingGroup = getMatchingGroup(groups, user_groups ?? [])
    if (matchingGroup) {
      return matchingGroup
    }

    const matchingTag = getMatchingTag(configTags, tags)
    if (matchingTag) {
      return matchingTag
    }

    if (isPostCreator && uid) {
      return getGroupOrTagForUser(uid, groups, configGroups, configTags, tags)
    }

    return uid && users.includes(uid) ? uid : null
  }, [groups, user_groups, configTags, tags, isPostCreator, uid, configGroups, users])

  const chatRefId = chat?.ref_id
  const handleGetServiceCall = useCallback(async () => {
    if (!chatRefId) {
      return
    }

    const existingServiceCall = isExistServiceCall
      ? serviceCalls?.find((call) => call?.service_call_id === chatRefId) ?? null
      : null

    if (existingServiceCall) {
      setSelectedServiceCall(existingServiceCall)
      serviceCallModal.onOpen()
      return
    }

    const contextId = calculateContextIdForGetServiceCall()
    if (!contextId) {
      return
    }

    const apiServiceCall = await dispatch(getServiceCall({ contextId, service_call_id: chatRefId }))

    if (apiServiceCall) {
      setSelectedServiceCall(apiServiceCall)
      serviceCallModal.onOpen()
    }
  }, [chatRefId, isExistServiceCall, serviceCalls, calculateContextIdForGetServiceCall, dispatch, serviceCallModal])

  const usersInChat = chat?.post_origin_users ?? []
  const groupUsersInChat = chat?.group_users ?? []

  const uniqUids = Array.from(new Set([...groupUsersInChat, ...usersInChat])).filter((uid) =>
    Object.keys(retailUsersObject!).includes(uid)
  )

  const usersNames =
    uniqUids.map(
      (u) =>
        retailUsersObject?.[u]?.first_name +
        ' ' +
        (retailUsersObject?.[u]?.last_name ? retailUsersObject?.[u]?.last_name : '')
    ) ?? []

  const chatAudience = usersNames.join(', ')
  const trimmedAudience = trimAndCountExtraGroups({ items: chatAudience, sliceLimit: 5 })

  const audienceItems = useMemo(() => chatAudience.split(','), [chatAudience])

  const isAnnouncementPost = useMemo(() => chat.type === keys.POST_TYPE.GROUP_POST_ANNOUNCEMENT, [chat.type])

  return (
    <>
      <Flex justifyContent="space-between" alignItems="center" style={{ columnGap: '10px' }} w="100%" pos="relative">
        {isExistServiceCall && (
          <NoFocusButton
            pos="absolute"
            bg={SERVICE_CALL_BUTTON_COLORS.bg}
            p="5px"
            h="unset"
            minW="unset"
            left={isRtl ? SERVICE_CALL_BUTTON_POSITIONS.left : 'unset'}
            right={isRtl ? 'unset' : SERVICE_CALL_BUTTON_POSITIONS.left}
            top={SERVICE_CALL_BUTTON_POSITIONS.top}
            style={{ columnGap: '10px' }}
            cursor="pointer"
            onClick={handleGetServiceCall}
            isDisabled={isLoading}
            isLoading={isLoading}
          >
            <Wrench style={{ fill: SERVICE_CALL_BUTTON_COLORS.iconFill }} width="20px" height="20px" />
          </NoFocusButton>
        )}

        <Flex alignItems="center" style={{ columnGap: '10px' }}>
          <ChevronLeft
            onClick={onClose}
            cursor="pointer"
            size={20}
            style={{ transform: isRtl ? 'rotate(180deg)' : 'rotate(0deg)' }}
          />
          <Avatar src={images?.[0]} w="50px" h="50px" />
          <Flex flexDir="column">
            <Text fontWeight="bold" fontSize="md">
              {title}
            </Text>
            <Flex alignItems="center" py="5px">
              <Tag bg={theme?.elementsColor} color="white" alignContent="center" minH="unset" borderRadius="10px">
                <Text fontSize="xs">
                  <LocaleText text="search_feed_creator_filter" />
                </Text>
              </Tag>
              <Text fontWeight="bold" isTruncated maxW="450px" fontSize="sm" w="100%">
                &nbsp;
                {chatCreator}
              </Text>
            </Flex>

            <TooltipList items={audienceItems}>
              <Text fontSize="sm">{trimmedAudience}</Text>
            </TooltipList>
          </Flex>
        </Flex>

        {canEditPost && (
          <PostMenu
            isGroup={isGroupPost}
            groups={groups}
            users={users}
            post_id={chatId}
            editRole={isPostCreator}
            onEditPost={editPostModal.onOpen}
            isShowAdminsModalMenuItem={isAnnouncementPost}
            onSetAdminModalOpen={setAdminModal.onOpen}
            isAdminPostPermission={isAdminPostPermission}
          />
        )}
      </Flex>

      {editPostModal.isOpen && (
        <PostModal
          isOpen={editPostModal.isOpen}
          onClose={editPostModal.onClose}
          postType={chat.type}
          postTitle={chat.title}
          isChat={true}
          isEditing={true}
          {...chat}
        />
      )}

      {setAdminModal.isOpen && (
        <PostAdminsModal isOpen={setAdminModal.isOpen} onClose={setAdminModal.onClose} post={chat} />
      )}

      {selectedServiceCall && serviceCallModal.isOpen && (
        <ServicaCallOveviewModal
          isOpen={serviceCallModal.isOpen}
          onClose={serviceCallModal.onClose}
          serviceCallViewId={selectedServiceCall.view}
          serviceCallNumber={selectedServiceCall.service_call_number}
        />
      )}
    </>
  )
})

const getMatchingGroup = (groups: string[], userGroups: string[]): string | undefined =>
  groups.find((group) => userGroups?.includes(group))

const getMatchingTag = (configTags: ITag[], tags: string[]): string | null =>
  configTags.find((tag) => tags.includes(tag.sk))?.sk ?? null

const getGroupOrTagForUser = (
  uid: string,
  groups: string[],
  configGroups: IConfigStateGroups,
  configTags: ITag[],
  tags: string[]
): string | null => {
  const groupId = findGroupByUid(groups, configGroups, uid)
  if (groupId) {
    return groupId
  }

  const currentTags = configTags.filter((tag) => tags.includes(tag.sk))
  const tagId = findTagByUid(currentTags, configGroups, uid)
  return tagId || null
}
